/* ==============================================================================
   Captura de tela — múltiplas fontes simultâneas
   ==============================================================================
   O requisito é compartilhar VÁRIAS telas ou janelas ao mesmo tempo, cada uma
   como um track independente.

   O que o navegador impõe, e como isso molda a API
   ===============================================
   `getDisplayMedia()` abre o seletor NATIVO do sistema, e ele escolhe UMA fonte
   por chamada. Não existe API para "me dê estas três telas": para três fontes,
   três chamadas, três diálogos.

   Consequência de produto: a interface precisa deixar claro que cada clique em
   "adicionar tela" abre um seletor. Um botão que abre três diálogos em sequência
   parece um bug.

   Consequência técnica: cada chamada exige um GESTO do usuário. Chamar
   `getDisplayMedia` fora de um handler de clique é rejeitado pelo navegador —
   então não é possível "restaurar as telas compartilhadas" ao reconectar sem
   pedir de novo.

   O usuário pode parar pela interface do navegador
   ================================================
   Chrome mostra uma barra "Você está compartilhando…" com um botão de parar. Ele
   não passa pelo nosso código: o track simplesmente termina. Por isso todo track
   capturado aqui recebe um ouvinte de `ended` — sem ele, a interface continuaria
   mostrando "compartilhando" depois de a pessoa ter parado, e o par continuaria
   esperando vídeo.
   ============================================================================== */

import type { LocalTrack, TrackPurpose } from './mesh.ts';

export interface CaptureOptions {
  /**
   * Dica de conteúdo. Muda a otimização do codificador.
   *
   * `detail` para texto e IDE: preserva nitidez, sacrifica fluidez.
   * `motion` para vídeo e jogo: preserva fluidez, sacrifica nitidez.
   *
   * A escolha é do usuário POR FONTE, e não global: compartilhar um documento e
   * um vídeo ao mesmo tempo é comum, e a otimização certa é oposta nos dois.
   */
  contentHint: 'detail' | 'motion';
  /** Tenta capturar o áudio do sistema junto. Suporte irregular. */
  withSystemAudio: boolean;
  /** Teto de resolução. `null` = o que a fonte oferecer. */
  maxHeight: number | null;
  maxFramerate: number;
}

export const DEFAULT_CAPTURE: CaptureOptions = {
  contentHint: 'detail',
  withSystemAudio: false,
  // 1080p como teto padrão: acima disso o custo de banda em malha cresce sem
  // ganho de legibilidade, porque o visualizador quase nunca tem a tela inteira
  // dedicada a uma fonte.
  maxHeight: 1080,
  maxFramerate: 30,
};

export interface CaptureResult {
  /** O track de vídeo, pronto para entrar na malha. */
  video: LocalTrack;
  /**
   * Áudio do sistema, quando o navegador entregou.
   *
   * `null` é o caso COMUM, não o excepcional: no Firefox e no Safari não existe;
   * no Chrome só funciona ao compartilhar uma aba ou a tela inteira, e no macOS
   * exige permissão de gravação de tela concedida ao navegador.
   */
  systemAudio: LocalTrack | null;
  /** Explicação quando o áudio do sistema foi pedido e não veio. */
  systemAudioNotice: string | null;
}

/** Erro que distingue "o usuário cancelou" de "falhou". */
export class CaptureCancelled extends Error {
  constructor() {
    super('Captura cancelada pelo usuário.');
    this.name = 'CaptureCancelled';
  }
}

let sourceCounter = 0;

/**
 * Captura uma fonte de tela.
 *
 * Devolve um `LocalTrack` pronto para `mesh.addLocalTrack`. O `sourceId` é
 * gerado aqui e é o que liga o track ao rótulo anunciado pelo canal de controle —
 * é assim que o par remoto sabe que aquele vídeo é "Tela 2" e não a câmera.
 */
export async function captureScreen(
  options: Partial<CaptureOptions> = {},
  onEnded?: (sourceId: string) => void,
): Promise<CaptureResult> {
  const config = { ...DEFAULT_CAPTURE, ...options };

  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error(
      'Este navegador não suporta compartilhamento de tela. Use o aplicativo de ' +
        'desktop ou um navegador baseado em Chromium.',
    );
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        ...(config.maxHeight !== null ? { height: { max: config.maxHeight } } : {}),
        frameRate: { max: config.maxFramerate },
      },
      /*
       * Pedir áudio quando o usuário quer. O navegador decide se entrega.
       *
       * Pedir e não receber é o caso normal e NÃO é erro: a promessa de
       * `getDisplayMedia` resolve com o vídeo mesmo sem o áudio. Tratar a
       * ausência como falha impediria o compartilhamento de tela em todo
       * navegador que não suporta áudio do sistema.
       */
      audio: config.withSystemAudio,
    });
  } catch (error) {
    /*
     * `NotAllowedError` significa duas coisas diferentes.
     *
     * O usuário clicou em "cancelar" no seletor, OU a permissão foi negada
     * permanentemente. Os dois chegam como o mesmo erro, e a distinção não está
     * disponível. Tratar como cancelamento é a leitura mais provável e a menos
     * alarmante — uma mensagem de "permissão negada" para quem só desistiu
     * seria confusa.
     */
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      throw new CaptureCancelled();
    }
    throw error;
  }

  const [videoTrack] = stream.getVideoTracks();
  if (!videoTrack) {
    for (const track of stream.getTracks()) track.stop();
    throw new Error('A captura não produziu vídeo.');
  }

  sourceCounter += 1;
  const sourceId = `screen-${sourceCounter}`;

  videoTrack.contentHint = config.contentHint;

  /*
   * O rótulo vem do track, quando o navegador o fornece.
   *
   * O Chrome coloca algo como "window:12345:0" ou o título da janela — o
   * primeiro é inútil para humano, o segundo é ótimo. Um rótulo que começa com
   * "screen:" ou "window:" é descartado em favor de um número sequencial, que
   * ao menos é legível.
   */
  const rawLabel = videoTrack.label;
  const usable =
    rawLabel.length > 0 &&
    !rawLabel.startsWith('screen:') &&
    !rawLabel.startsWith('window:') &&
    !/^\d+:\d+$/.test(rawLabel);

  const label = usable ? rawLabel : `Tela ${sourceCounter}`;

  /*
   * O usuário pode parar pela barra do navegador, fora do nosso código.
   *
   * Sem este ouvinte, a interface continuaria mostrando "compartilhando" e o
   * par continuaria com um vídeo congelado. É o modo de falha mais comum de
   * compartilhamento de tela mal implementado.
   */
  videoTrack.addEventListener('ended', () => {
    onEnded?.(sourceId);
  });

  const audioTracks = stream.getAudioTracks();
  let systemAudio: LocalTrack | null = null;
  let systemAudioNotice: string | null = null;

  if (config.withSystemAudio) {
    const [audioTrack] = audioTracks;
    if (audioTrack) {
      audioTrack.contentHint = 'music';
      systemAudio = {
        track: audioTrack,
        purpose: 'microphone',
        sourceId: `${sourceId}-audio`,
        label: `${label} (áudio)`,
      };
    } else {
      systemAudioNotice =
        'Este navegador não entregou o áudio do sistema. No Chrome, funciona ao ' +
        'compartilhar uma aba ou a tela inteira; no macOS exige permissão de ' +
        'gravação de tela. O aplicativo de desktop captura de forma confiável.';
    }
  } else {
    // Áudio não pedido, mas veio: para o track em vez de deixar capturando
    // silenciosamente. Um track de áudio ativo que ninguém usa é captura de
    // áudio sem consentimento explícito.
    for (const track of audioTracks) track.stop();
  }

  return {
    video: { track: videoTrack, purpose: 'screen', sourceId, label },
    systemAudio,
    systemAudioNotice,
  };
}

/** Captura a câmera. Uma só, ao contrário das telas. */
export async function captureCamera(
  deviceId: string | null,
  maxHeight = 720,
): Promise<LocalTrack> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      ...(deviceId !== null ? { deviceId: { exact: deviceId } } : {}),
      height: { ideal: maxHeight },
      frameRate: { ideal: 30, max: 30 },
    },
    audio: false,
  });

  const [track] = stream.getVideoTracks();
  if (!track) throw new Error('A câmera não produziu vídeo.');

  track.contentHint = 'motion';
  return {
    track,
    purpose: 'camera' as TrackPurpose,
    sourceId: 'camera',
    label: 'Câmera',
  };
}

/** Lista as câmeras disponíveis. */
export async function listCameras(): Promise<Array<{ deviceId: string; label: string }>> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) => d.kind === 'videoinput')
    .map((d, index) => ({
      deviceId: d.deviceId,
      label: d.label || `Câmera ${index + 1}`,
    }));
}
